// src/components/popups/GraphsExplainerDrawer.js
import React from 'react';
import './GraphsExplainerDrawer.css';

const COPY = {
  signups: {
    title: 'Signups',
    body: [
      'What it is: how many NEW users registered on each day.',
      'If it’s going up: your visibility, referrals, listings, or marketing is working.',
      'If it’s going down: fewer new people are discovering the app (or signup is failing).',
      'Try this: post announcements, improve onboarding, fix friction (login / verification), and push one clear “share BarterFi” campaign.',
    ],
  },

  dau: {
    title: 'DAU (Daily Active Users)',
    body: [
      'What it is: unique users who opened the app at least once today (last 24 hours). Each person counts once.',
      'If it’s going up: you’re building daily habit — people have a reason to come back.',
      'If it’s going down: users are not returning daily (or there’s friction: downtime, slow load, login problems).',
      'Try this: check uptime + API latency, improve “home feed freshness”, add daily triggers (stories, trending posts, badges, reminders).',
    ],
  },

  wau: {
    title: 'WAU (Weekly Active Users)',
    body: [
      'What it is: unique users who opened the app at least once in the last 7 days. Each person counts once in that window.',
      'If it’s going up: your audience is expanding and/or retention is improving across the week.',
      'If it’s going down: week-to-week retention is weakening (people don’t find enough value to return).',
      'Try this: improve retention loops (follow suggestions, creators to watch, notifications), and reduce “dead feed” moments with better discovery.',
    ],
  },

  stickiness: {
    title: 'Stickiness (DAU / WAU %)',
    body: [
      'What it is: (DAU ÷ WAU) × 100. It shows how often weekly users come back daily.',
      'If it’s going up: your weekly users are turning into daily users — strong habit.',
      'If it’s going down: users might still open weekly, but fewer are coming daily (habit is weak).',
      'Try this: add daily reasons to engage (stories, streak/badge goals, trending topics), and make the “first 10 seconds” experience addictive.',
    ],
  },

  // ─────────────────────────────────────
  // ✅ Engagement graphs
  // ─────────────────────────────────────
  avgPostsPerDay: {
    title: 'Avg Posts / Day',
    body: [
      'What it is: how many posts are being created per day (content supply).',
      'If it’s going up: more people are posting — creators are active and the app feels alive.',
      'If it’s going down: fewer people feel motivated to post, or the app feels quiet to creators.',
      'Try this: reduce posting friction (upload speed, drafts), reward posting (badges/boost), and give clear prompts (topic of the day, challenges, “post to earn visibility”).',
    ],
  },

  avgLikesPerPost: {
    title: 'Avg Likes / Post',
    body: [
      'What it is: total likes ÷ total posts (per day). It measures “likeability” per post, not total volume.',
      'If it’s going up: posts are landing better, or the feed is distributing content to the right users.',
      'If it’s going down: content quality/relevance is weaker, reach is lower, or users are scrolling but not reacting.',
      'Try this: feature best posts/creators, boost high-like posts in ranking, improve discovery relevance, and encourage better formats (images, strong hooks, clear captions).',
    ],
  },

  avgCommentsPerPost: {
    title: 'Avg Comments / Post',
    body: [
      'What it is: total comments ÷ total posts (per day). Comments signal deeper engagement than likes.',
      'If it’s going up: posts are triggering conversation — community energy is stronger.',
      'If it’s going down: fewer discussion-worthy posts, fewer active commenters online, or conversations are dying early.',
      'Try this: push question-style posts, add “prompt of the day”, highlight debates, and notify followers fast so comments start early and snowball.',
    ],
  },

  engagementDensity: {
    title: 'Engagement Density (Likes + Comments) / Post',
    body: [
      'What it is: (likes + comments) ÷ posts (per day). It’s the overall engagement per post score.',
      'If it’s going up: the feed is healthier — people react more per post and content is matching the audience.',
      'If it’s going down: posts are being created but not interacted with (reach is down), or there’s a spike of low-engagement posting.',
      'Try this: improve feed relevance, reduce low-quality/spam posts, boost proven content, and make engagement easier (quick reactions, comment prompts, better notifications).',
    ],
  },
};

export default function GraphsExplainerDrawer({ open, onClose, type = 'signups' }) {
  if (!open) return null;

  const content = COPY[type] || COPY.signups;

  return (
    <div className="ged-backdrop" onClick={onClose}>
      <div className="ged-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="ged-handle" />
        <div className="ged-header">
          <div className="ged-title">{content.title}</div>
          <button className="ged-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="ged-body">
          {content.body.map((t, idx) => (
            <div key={idx} className="ged-line">• {t}</div>
          ))}
        </div>

        <div className="ged-footer">
          <button className="ged-btn" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}
